import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/data/status-badge";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, ExternalLink, Fuel, Pencil, Trash2, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/layout/page-header";
import { PageTabs } from "@/components/layout/page-tabs";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { EmptyState } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { useSetores } from "@/hooks/use-setores";
import { useFuncionarios } from "@/hooks/use-equipe";
import { useVinculosAtivos, useQuinzenaAtiva } from "@/hooks/use-equipe";
import { useFaixasAuditoria, nota1 } from "@/hooks/use-auditoria";
import {
  useTaxasRemuneracao, useCreateTaxaRemuneracao, useUpdateTaxaRemuneracao, useDeleteTaxaRemuneracao,
  useTaxaSetoresRemuneracao, useReplaceTaxaSetoresRemuneracao,
  useAuxiliosRecorrentesRemuneracao, useCreateAuxilioRecorrenteRemuneracao,
  useUpdateAuxilioRecorrenteRemuneracao, useGerarAuxiliosRemuneracao,
  useSimulacaoRemuneracao,
  parseErroRemuneracao,
  type ExcecaoTaxaSetor, type RemTaxaComUso, type PessoaSimulacaoRemuneracao,
} from "@/hooks/use-remuneracao";
import { competenciaLabel, nota2, DestaqueNota } from "@/components/equipe/nota-mensal";

/** Trackpad rolando a página com o campo focado decrementa o número — sempre desfoca no scroll. */
function semScroll(e: React.WheelEvent<HTMLInputElement>) {
  e.currentTarget.blur();
}

function formatarBRL(v: string | number | null | undefined): string {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? 0));
  return (Number.isFinite(n) ? n : 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = iso.slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

// ─── Aba Taxas ──────────────────────────────────────────────────────────────

function NovaTaxaDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast();
  const create = useCreateTaxaRemuneracao();
  const [vigenciaInicio, setVigenciaInicio] = useState("");
  const [valorFixoHa, setValorFixoHa] = useState("");
  const [valorVariavelHa, setValorVariavelHa] = useState("");

  const podeSalvar = !!vigenciaInicio && !!valorFixoHa && !!valorVariavelHa;

  const salvar = async () => {
    if (!podeSalvar) return;
    try {
      await create.mutateAsync({ vigenciaInicio, valorFixoHa, valorVariavelHa });
      toast({ title: "Nova vigência criada" });
      onOpenChange(false);
      setVigenciaInicio(""); setValorFixoHa(""); setValorVariavelHa("");
    } catch (e: any) {
      toast({ title: "Erro ao criar vigência", description: e?.message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Nova vigência</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>A partir de</Label>
            <Input type="date" className="h-11" value={vigenciaInicio} onChange={e => setVigenciaInicio(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Valor fixo / ha</Label>
            <Input
              type="number" step="0.01" min="0" inputMode="decimal" className="h-11"
              value={valorFixoHa} onChange={e => setValorFixoHa(e.target.value)} onWheel={semScroll}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Valor variável / ha</Label>
            <Input
              type="number" step="0.01" min="0" inputMode="decimal" className="h-11"
              value={valorVariavelHa} onChange={e => setValorVariavelHa(e.target.value)} onWheel={semScroll}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Uma vigência nunca é editada depois de criada — um reajuste sempre cria outra.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={salvar} disabled={!podeSalvar || create.isPending}>
            {create.isPending ? "Criando..." : "Criar vigência"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExcecaoSetorDialog({ open, onOpenChange, remTaxaId, setorId, setorNome, existente }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  remTaxaId: string; setorId: string; setorNome: string;
  existente?: ExcecaoTaxaSetor;
}) {
  const { toast } = useToast();
  const { data: excecoes = [] } = useTaxaSetoresRemuneracao(remTaxaId);
  const replace = useReplaceTaxaSetoresRemuneracao();
  const [valorFixoHa, setValorFixoHa] = useState(existente?.valorFixoHa ?? "");
  const [valorVariavelHa, setValorVariavelHa] = useState(existente?.valorVariavelHa ?? "");
  const [observacao, setObservacao] = useState(existente?.observacao ?? "");

  const podeSalvar = observacao.trim().length > 0 && (!!valorFixoHa || !!valorVariavelHa);

  const salvar = async () => {
    if (!podeSalvar) return;
    const outras = excecoes.filter(e => e.setorId !== setorId).map(e => ({
      setorId: e.setorId, valorFixoHa: e.valorFixoHa, valorVariavelHa: e.valorVariavelHa, observacao: e.observacao,
    }));
    try {
      await replace.mutateAsync({
        remTaxaId,
        excecoes: [...outras, {
          setorId, valorFixoHa: valorFixoHa || null, valorVariavelHa: valorVariavelHa || null, observacao: observacao.trim(),
        }],
      });
      toast({ title: "Exceção salva" });
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "Erro ao salvar exceção", description: e?.message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Exceção — {setorNome}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Valor fixo / ha (deixe em branco para usar a taxa geral)</Label>
            <Input
              type="number" step="0.01" min="0" inputMode="decimal" className="h-11"
              value={valorFixoHa} onChange={e => setValorFixoHa(e.target.value)} onWheel={semScroll}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Valor variável / ha (deixe em branco para usar a taxa geral)</Label>
            <Input
              type="number" step="0.01" min="0" inputMode="decimal" className="h-11"
              value={valorVariavelHa} onChange={e => setValorVariavelHa(e.target.value)} onWheel={semScroll}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Observação (obrigatória)</Label>
            <Textarea rows={2} value={observacao} onChange={e => setObservacao(e.target.value)} placeholder="Por que este setor é diferente" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={salvar} disabled={!podeSalvar || replace.isPending}>
            {replace.isPending ? "Salvando..." : "Salvar exceção"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const COLUNAS_TAXA_SETOR = [null, "5rem", "7rem", "9rem"];

/** Texto de confirmação quando a vigência já foi consumida por um fechamento — o snapshot protege o pagamento, isto é só aviso. */
function fraseVigenciaUsada(usadaEm: string, acao: "editar" | "apagar"): string {
  const mes = usadaEm.slice(5, 7);
  const ano = usadaEm.slice(0, 4);
  if (acao === "editar") {
    return `Esta vigência foi usada no fechamento de ${mes}/${ano}. Editar NÃO altera o que já foi pago — aquele fechamento continua com os valores congelados. Mas a taxa deixa de explicar aquele número.`;
  }
  return `Esta vigência foi usada no fechamento de ${mes}/${ano}. Apagar NÃO desfaz o que já foi pago — aquele fechamento continua com os valores congelados. Mas você perde o registro de qual taxa gerou aquele número, e isto não pode ser desfeito.`;
}

/**
 * Uma vigência: sempre editável e apagável — mesmo já CONSUMIDA por um
 * fechamento (`usadaEm`). `fechamento_mensal` é snapshot: editar/apagar a
 * taxa depois não recalcula nada do que já foi pago, só faz a taxa parar de
 * explicar aquele número histórico. Por isso o cadeado é só INFORMAÇÃO — os
 * controles continuam visíveis, e a única diferença é uma confirmação
 * explícita antes de salvar/apagar dizendo o que se perde.
 */
function VigenciaRow({ taxa, destaque }: { taxa: RemTaxaComUso; destaque: boolean }) {
  const { toast } = useToast();
  const update = useUpdateTaxaRemuneracao();
  const del = useDeleteTaxaRemuneracao();
  const [editando, setEditando] = useState(false);
  const [valorFixoHa, setValorFixoHa] = useState(taxa.valorFixoHa);
  const [valorVariavelHa, setValorVariavelHa] = useState(taxa.valorVariavelHa);
  const [apagando, setApagando] = useState(false);
  const [confirmandoEdicao, setConfirmandoEdicao] = useState(false);

  const bloqueada = !!taxa.usadaEm;
  const explicacao = taxa.usadaEm
    ? `usada no fechamento de ${taxa.usadaEm.slice(5, 7)}/${taxa.usadaEm.slice(0, 4)}`
    : null;

  const iniciarEdicao = () => {
    setValorFixoHa(taxa.valorFixoHa);
    setValorVariavelHa(taxa.valorVariavelHa);
    setEditando(true);
  };

  const executarSalvar = async () => {
    try {
      await update.mutateAsync({ id: taxa.id, data: { valorFixoHa, valorVariavelHa } });
      toast({ title: "Vigência atualizada" });
      setEditando(false);
      setConfirmandoEdicao(false);
    } catch (e) {
      const erro = parseErroRemuneracao(e, "Erro ao editar vigência");
      toast({ title: "Erro ao editar vigência", description: erro.message, variant: "destructive" });
    }
  };

  const salvar = () => {
    if (bloqueada) { setConfirmandoEdicao(true); return; }
    executarSalvar();
  };

  const apagar = async () => {
    try {
      await del.mutateAsync(taxa.id);
      toast({ title: "Vigência apagada" });
      setApagando(false);
    } catch (e) {
      const erro = parseErroRemuneracao(e, "Erro ao apagar vigência");
      toast({ title: "Erro ao apagar vigência", description: erro.message, variant: "destructive" });
      setApagando(false);
    }
  };

  if (editando) {
    return (
      <DataCard
        titulo={`A partir de ${dataBR(taxa.vigenciaInicio)}`}
        estado={destaque ? <StatusBadge tom="sucesso">atual</StatusBadge> : undefined}
        testId={`taxa-${taxa.id}`}
        padded
      >
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Fixo / ha</Label>
            <Input
              type="number" step="0.01" min="0" inputMode="decimal" className="h-10"
              value={valorFixoHa} onChange={e => setValorFixoHa(e.target.value)} onWheel={semScroll}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Variável / ha</Label>
            <Input
              type="number" step="0.01" min="0" inputMode="decimal" className="h-10"
              value={valorVariavelHa} onChange={e => setValorVariavelHa(e.target.value)} onWheel={semScroll}
            />
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditando(false)} disabled={update.isPending}>
            Cancelar
          </Button>
          <Button
            size="sm" className="flex-1" onClick={salvar}
            disabled={update.isPending || !valorFixoHa || !valorVariavelHa}
          >
            {update.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </div>

        <AlertDialog open={confirmandoEdicao} onOpenChange={setConfirmandoEdicao}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar edição</AlertDialogTitle>
              <AlertDialogDescription>
                {taxa.usadaEm && fraseVigenciaUsada(taxa.usadaEm, "editar")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={update.isPending}>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={e => { e.preventDefault(); executarSalvar(); }} disabled={update.isPending}>
                {update.isPending ? "Salvando..." : "Salvar mesmo assim"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DataCard>
    );
  }

  return (
    <DataCard
      titulo={`A partir de ${dataBR(taxa.vigenciaInicio)}`}
      meta={`${formatarBRL(taxa.valorFixoHa)} fixo/ha · ${formatarBRL(taxa.valorVariavelHa)} variável/ha`}
      estado={destaque ? <StatusBadge tom="sucesso">atual</StatusBadge> : undefined}
      acao={
        <div className="flex items-center gap-1">
          {bloqueada && <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-label="Vigência já usada em um fechamento" />}
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={iniciarEdicao} aria-label="Editar vigência">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive"
            onClick={() => setApagando(true)} aria-label="Apagar vigência"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      }
      aviso={explicacao ? `${explicacao} — editar ou apagar não altera o que já foi pago.` : undefined}
      testId={`taxa-${taxa.id}`}
    >
      {null}

      <AlertDialog open={apagando} onOpenChange={setApagando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar vigência</AlertDialogTitle>
            <AlertDialogDescription>
              A partir de <strong>{dataBR(taxa.vigenciaInicio)}</strong>: {formatarBRL(taxa.valorFixoHa)} fixo/ha e{" "}
              {formatarBRL(taxa.valorVariavelHa)} variável/ha. Esta ação não pode ser desfeita.
              {taxa.usadaEm && <><br /><br />{fraseVigenciaUsada(taxa.usadaEm, "apagar")}</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => { e.preventDefault(); apagar(); }}
              disabled={del.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {del.isPending ? "Apagando..." : "Apagar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DataCard>
  );
}

function TaxasTab() {
  const { data: taxas = [], isLoading } = useTaxasRemuneracao();
  const { setores: todosSetores = [] } = useSetores({ somenteProdutivos: true });
  // Setor desativado não entra na conferência — "C1 e C2" com 0,00 ha só
  // polui a tabela, e não tem encarregado nem taxa cabendo nele.
  const setores = useMemo(() => todosSetores.filter(s => s.ativo), [todosSetores]);
  const [novaOpen, setNovaOpen] = useState(false);
  const [excecaoSetor, setExcecaoSetor] = useState<{ id: string; nome: string } | null>(null);

  const atual: RemTaxaComUso | undefined = taxas[0];
  const { data: excecoes = [] } = useTaxaSetoresRemuneracao(atual?.id);
  const excecaoPorSetor = useMemo(() => new Map(excecoes.map(e => [e.setorId, e])), [excecoes]);

  if (isLoading) return <PageSkeleton variante="tabela" />;

  return (
    <div className="space-y-4">
      <Button className="w-full h-11" onClick={() => setNovaOpen(true)}>
        <Plus className="h-4 w-4 mr-1.5" />Nova vigência
      </Button>

      {taxas.length === 0 ? (
        <EmptyState titulo="Nenhuma taxa cadastrada" descricao="Crie a primeira vigência para começar." />
      ) : (
        <div className="space-y-2">
          {taxas.map((t, i) => (
            <VigenciaRow key={t.id} taxa={t} destaque={i === 0} />
          ))}
        </div>
      )}

      {atual && (
        <DataCard
          titulo="Valores derivados por setor"
          meta="Conferência, não cadastro — os valores são calculados, não digitados."
          padded={false}
        >
          <DataTable colunas={COLUNAS_TAXA_SETOR}>
            <DataTableHeader>
              <DataTableRow>
                <DataTableHead>Setor</DataTableHead>
                <DataTableHead num>Área</DataTableHead>
                <DataTableHead num>Fixo/mês</DataTableHead>
                <DataTableHead num>Variável/mês (teto)</DataTableHead>
              </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
              {setores.map(s => {
                const excecao = excecaoPorSetor.get(s.id);
                const fixoHa = excecao?.valorFixoHa ?? atual.valorFixoHa;
                const variavelHa = excecao?.valorVariavelHa ?? atual.valorVariavelHa;
                const area = parseFloat(String(s.areaHectares ?? 0));
                return (
                  <DataTableRow
                    key={s.id}
                    className="cursor-pointer"
                    onClick={() => setExcecaoSetor({ id: s.id, nome: s.nome })}
                  >
                    <DataTableCell>
                      {s.nome}
                      {excecao && <Badge variant="outline" className="ml-1.5 text-[10px]">exceção</Badge>}
                    </DataTableCell>
                    <DataTableNum>{area.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} ha</DataTableNum>
                    <DataTableNum className="font-semibold text-foreground">{formatarBRL(area * Number(fixoHa))}</DataTableNum>
                    <DataTableNum>{formatarBRL(area * Number(variavelHa))}</DataTableNum>
                  </DataTableRow>
                );
              })}
            </DataTableBody>
          </DataTable>
        </DataCard>
      )}

      <NovaTaxaDialog open={novaOpen} onOpenChange={setNovaOpen} />
      {atual && excecaoSetor && (
        <ExcecaoSetorDialog
          open={!!excecaoSetor}
          onOpenChange={v => !v && setExcecaoSetor(null)}
          remTaxaId={atual.id}
          setorId={excecaoSetor.id}
          setorNome={excecaoSetor.nome}
          existente={excecaoPorSetor.get(excecaoSetor.id)}
        />
      )}
    </div>
  );
}

// ─── Aba Auxílios ───────────────────────────────────────────────────────────

function NovoAuxilioDialog({ open, onOpenChange, funcionarioId, funcionarioNome }: {
  open: boolean; onOpenChange: (v: boolean) => void; funcionarioId: string; funcionarioNome: string;
}) {
  const { toast } = useToast();
  const create = useCreateAuxilioRecorrenteRemuneracao();
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");

  const podeSalvar = descricao.trim().length > 0 && !!valor;

  const salvar = async () => {
    if (!podeSalvar) return;
    try {
      await create.mutateAsync({ funcionarioId, descricao: descricao.trim(), valor });
      toast({ title: "Auxílio recorrente criado" });
      onOpenChange(false);
      setDescricao(""); setValor("");
    } catch (e: any) {
      toast({ title: "Erro ao criar auxílio", description: e?.message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Novo auxílio — {funcionarioNome}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Descrição</Label>
            <Input value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Auxílio combustível" />
          </div>
          <div className="space-y-1.5">
            <Label>Valor mensal</Label>
            <Input
              type="number" step="0.01" min="0" inputMode="decimal" className="h-11"
              value={valor} onChange={e => setValor(e.target.value)} onWheel={semScroll}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={salvar} disabled={!podeSalvar || create.isPending}>
            {create.isPending ? "Criando..." : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuxiliosTab() {
  const { toast } = useToast();
  const { data: funcionarios = [] } = useFuncionarios();
  const { data: vinculos = [] } = useVinculosAtivos();
  const { data: recorrentes = [], isLoading } = useAuxiliosRecorrentesRemuneracao();
  const { data: quinzenaAtiva } = useQuinzenaAtiva();
  const updateRecorrente = useUpdateAuxilioRecorrenteRemuneracao();
  const gerar = useGerarAuxiliosRemuneracao();
  const [novoPara, setNovoPara] = useState<{ id: string; nome: string } | null>(null);

  const encarregadoIds = useMemo(() => new Set(vinculos.map(v => v.funcionarioId)), [vinculos]);
  const encarregados = useMemo(
    () => funcionarios.filter(f => encarregadoIds.has(f.id)),
    [funcionarios, encarregadoIds],
  );

  const recorrentesPorFuncionario = useMemo(() => {
    const m = new Map<string, typeof recorrentes>();
    for (const r of recorrentes) {
      if (!r.ativo) continue;
      const lista = m.get(r.funcionarioId) ?? [];
      lista.push(r);
      m.set(r.funcionarioId, lista);
    }
    return m;
  }, [recorrentes]);

  const handleGerar = async () => {
    if (!quinzenaAtiva) return;
    try {
      const r = await gerar.mutateAsync(quinzenaAtiva.id);
      toast({ title: r.criados > 0 ? `${r.criados} auxílio(s) lançado(s)` : "Nenhum auxílio novo — já estavam gerados este mês" });
    } catch (e: any) {
      toast({ title: "Erro ao gerar auxílios", description: e?.message, variant: "destructive" });
    }
  };

  if (isLoading) return <PageSkeleton variante="cards" />;

  return (
    <div className="space-y-4">
      <DataCard titulo="Gerar auxílios do mês" meta="Cria os lançamentos que faltam na quinzena aberta, a partir dos recorrentes ativos abaixo." padded>
        <Button className="w-full h-11" onClick={handleGerar} disabled={!quinzenaAtiva || gerar.isPending}>
          <Fuel className="h-4 w-4 mr-1.5" />
          {gerar.isPending ? "Gerando..." : quinzenaAtiva ? "Gerar na quinzena atual" : "Nenhuma quinzena aberta"}
        </Button>
        <p className="text-xs text-muted-foreground mt-2">
          O lançamento sai na quinzena — os valores editados/detalhados ficam no{" "}
          <a href="/equipe/financeiro" className="underline inline-flex items-center gap-0.5">
            Financeiro <ExternalLink className="h-3 w-3" />
          </a>.
        </p>
      </DataCard>

      {encarregados.length === 0 ? (
        <EmptyState titulo="Nenhum encarregado ativo" descricao="Vincule alguém a um setor no cadastro de Funcionários." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {encarregados.map(f => {
            const meus = recorrentesPorFuncionario.get(f.id) ?? [];
            // Enquanto o modo for FIXO, o auxílio pode já estar embutido no
            // salário digitado — gerar o lançamento pagaria os mesmos R$200
            // duas vezes (quinzena + fechamento). Só aviso: a geração continua
            // liberada, o Hugo é quem confere e corrige o salário à mão.
            // Some sozinho quando o modo virar DERIVADO.
            const salarioAindaFixo = (f.salarioModo ?? "FIXO") !== "DERIVADO";
            const mostrarAviso = salarioAindaFixo && meus.length > 0;
            return (
              <DataCard
                key={f.id}
                titulo={f.apelido || f.nome}
                acao={
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setNovoPara({ id: f.id, nome: f.apelido || f.nome })}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Auxílio
                  </Button>
                }
                aviso={mostrarAviso
                  ? `salário ${formatarBRL(f.salarioMensal)} — confira se o auxílio já saiu do salário antes de gerar`
                  : undefined}
                padded
              >
                {meus.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem auxílio recorrente.</p>
                ) : (
                  <div className="space-y-1.5">
                    {meus.map(r => (
                      <div key={r.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="truncate">{r.descricao}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-medium">{formatarBRL(r.valor)}</span>
                          <Button
                            variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-destructive"
                            onClick={() => updateRecorrente.mutate({ id: r.id, data: { ativo: false } })}
                          >
                            desligar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </DataCard>
            );
          })}
        </div>
      )}

      {novoPara && (
        <NovoAuxilioDialog
          open={!!novoPara} onOpenChange={v => !v && setNovoPara(null)}
          funcionarioId={novoPara.id} funcionarioNome={novoPara.nome}
        />
      )}
    </div>
  );
}

// ─── Aba Faixas — espelho só de leitura de aud_faixa ───────────────────────

function FaixasTab() {
  const { data: faixas = [], isLoading } = useFaixasAuditoria();

  if (isLoading) return <PageSkeleton variante="tabela" />;

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Espelho de leitura — a faixa de nota é editada em{" "}
        <a href="/manejo/auditoria/faixas" className="underline inline-flex items-center gap-0.5">
          Critérios auditoria → Faixas de nota <ExternalLink className="h-3 w-3" />
        </a>.
      </p>
      {faixas.length === 0 ? (
        <EmptyState titulo="Nenhuma faixa cadastrada" />
      ) : (
        <DataCard titulo="Faixas vigentes" padded={false}>
          <DataTable colunas={[null, "6rem", "6rem"]}>
            <DataTableHeader>
              <DataTableRow>
                <DataTableHead>A partir da nota</DataTableHead>
                <DataTableHead num>% do fixo</DataTableHead>
                <DataTableHead num>% do variável</DataTableHead>
              </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
              {faixas.map(f => (
                <DataTableRow key={f.id}>
                  <DataTableCell>{nota1(f.notaMin)}{f.rotulo ? ` — ${f.rotulo}` : ""}</DataTableCell>
                  <DataTableNum>{f.percentualFixo}%</DataTableNum>
                  <DataTableNum>{f.percentualVariavel}%</DataTableNum>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        </DataCard>
      )}
    </div>
  );
}

// ─── Aba Simulação — só leitura, o racional do que cada pessoa vai receber ──
// Nunca grava nada e não é a Fase 3 (fechamento derivado) — é a conferência
// que hoje não existe: qual foi a nota, em que faixa caiu, e quanto isso vale
// em reais, no vocabulário que o Hugo usa com o encarregado.
//
// ⚠️ REVERSÃO (03/09/2026): a nota volta a ser ÚNICA por pessoa (média
// simples dos setores com fechamento) — não existe mais dinheiro por setor.
// A tabela de setor virou "as notas que compõem a média", com a conta
// explícita, e a nota/faixa/percentuais da pessoa ficam em destaque.

function PessoaSimulacaoCard({ pessoa, competencia }: { pessoa: PessoaSimulacaoRemuneracao; competencia: string }) {
  return (
    <DataCard
      titulo={`${pessoa.nome} · ${competenciaLabel(competencia)}`}
      meta={`Total: fixo ${formatarBRL(pessoa.fixo)} · variável ${formatarBRL(pessoa.variavel)} · ${pessoa.haDaPessoa.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} ha`}
      padded={false}
      testId={`simulacao-${pessoa.funcionarioId}`}
    >
      <div className="mx-3 mt-3">
        <DestaqueNota pessoa={pessoa} />
      </div>

      <div className="mt-3">
        <p className="text-xs font-medium text-muted-foreground px-3 mb-1">Notas que compõem a média</p>
        {pessoa.setores.length === 0 ? (
          <p className="text-sm text-muted-foreground px-3 pb-3">Sem setor.</p>
        ) : (
          <DataTable colunas={[null, "6rem"]}>
            <DataTableHeader>
              <DataTableRow>
                <DataTableHead>Setor</DataTableHead>
                <DataTableHead num>Nota</DataTableHead>
              </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
              {pessoa.setores.map(s => (
                <DataTableRow key={s.setorId}>
                  <DataTableCell>
                    {s.setorNome ?? "?"}
                    {s.foraDaMedia && <span className="text-xs text-muted-foreground ml-1.5">— fora da média (sem auditoria)</span>}
                  </DataTableCell>
                  <DataTableNum>{nota2(s.nota)}</DataTableNum>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}
      </div>
    </DataCard>
  );
}

function SimulacaoTab() {
  const [competencia, setCompetencia] = useState(() => new Date().toISOString().slice(0, 7));
  const { data, isLoading, isError, error } = useSimulacaoRemuneracao(competencia);

  const erro = isError ? parseErroRemuneracao(error, "Erro ao gerar simulação") : null;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label className="text-xs">Competência</Label>
        <Input type="month" className="h-11" value={competencia} onChange={e => setCompetencia(e.target.value)} />
      </div>

      <p className="text-xs text-muted-foreground">
        Só leitura — a conferência do que a nota de cada setor vale em reais. Não grava nada.
      </p>

      {isLoading ? (
        <PageSkeleton variante="cards" />
      ) : erro ? (
        <EmptyState
          titulo={erro.code === "TAXA_NAO_CADASTRADA" ? "Nenhuma taxa cadastrada" : "Erro ao gerar simulação"}
          descricao={erro.message}
        />
      ) : !data || data.porPessoa.length === 0 ? (
        <EmptyState titulo="Ninguém para simular" descricao="Nenhum encarregado com vínculo ativo a um setor." />
      ) : (
        <div className="space-y-3">
          {data.porPessoa.map(p => (
            <PessoaSimulacaoCard key={p.funcionarioId} pessoa={p} competencia={data.competencia} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Página ─────────────────────────────────────────────────────────────────

const ABAS = [
  { id: "taxas", label: "Taxas" },
  { id: "auxilios", label: "Auxílios" },
  { id: "faixas", label: "Faixas" },
  { id: "simulacao", label: "Simulação" },
];

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO_REMUNERACAO = { aba: "taxas" };
const PERMITIDOS_REMUNERACAO = { aba: ["taxas", "auxilios", "faixas", "simulacao"] } as const;

export default function EquipeRemuneracao() {
  const { estado, definir } = useEstadoNaUrl(PADRAO_REMUNERACAO, PERMITIDOS_REMUNERACAO);
  const aba = estado.aba;
  const setAba = (v: string) => definir({ aba: v });

  return (
    <div className="p-4 sm:p-6 space-y-4 pb-24">
      <PageHeader titulo="Remuneração" descricao="Taxa por hectare e auxílios do encarregado de setor" />
      <PageTabs abas={ABAS} ativa={aba} onChange={setAba} />
      <Tabs value={aba} onValueChange={setAba}>
        <TabsContent value="taxas"><TaxasTab /></TabsContent>
        <TabsContent value="auxilios"><AuxiliosTab /></TabsContent>
        <TabsContent value="faixas"><FaixasTab /></TabsContent>
        <TabsContent value="simulacao"><SimulacaoTab /></TabsContent>
      </Tabs>
    </div>
  );
}
